// Certificate mode: the bench is driven by the server. Every action is a
// request; the browser receives only what a student could see (spec 8.1).

import type { Action, ActivityView, Frame, HintView, RecordOutcome, View } from "./types";

export interface AttemptInfo { stage: number; stages: number; unit: number; level: number }

export interface Session {
  readonly remote: boolean;
  frame(): Frame;
  apply(action: Action): Promise<{ ok: boolean; error?: string }> | { ok: boolean; error?: string };
  record(capture: unknown, atMs?: number): void;
  recordReading(): Promise<RecordOutcome> | RecordOutcome;
  recordScope(): Promise<RecordOutcome> | RecordOutcome;
  predict(goal: string, value: number): Promise<{ ok: boolean; correct?: boolean; error?: string }> | { ok: boolean; correct?: boolean; error?: string };
  hint(): Promise<HintView | null> | HintView | null;
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok && !("ok" in (data as object))) throw new Error(data.error ?? `${r.status}`);
  return data;
}

export interface RemoteFrame extends Frame { attempt?: AttemptInfo }

/** A certificate attempt in progress on the server. */
export class RemoteSession implements Session {
  readonly remote = true;
  private last: RemoteFrame;
  private pending: unknown[] = [];
  private polling = false;
  private flushTimer: number | undefined;

  private constructor(readonly attemptId: string, first: RemoteFrame) {
    this.last = first;
  }

  static async start(unit: number, level: number): Promise<RemoteSession> {
    const r = await api<{ attemptId: string; frame: RemoteFrame; error?: string }>("POST", "/api/attempts", { unit, level });
    if (!r.attemptId) throw new Error(r.error ?? "could not start the attempt");
    return new RemoteSession(r.attemptId, r.frame);
  }

  frame(): RemoteFrame {
    return this.last;
  }

  attempt(): AttemptInfo | undefined {
    return this.last.attempt;
  }

  /** Fetch the current view (the server advances the simulation by real time). */
  async poll(): Promise<void> {
    if (this.polling) return;
    this.polling = true;
    try {
      const f = await api<RemoteFrame>("GET", `/api/attempts/${this.attemptId}`);
      if (f.bench) this.last = f;
    } catch {
      /* keep the last frame */
    } finally {
      this.polling = false;
    }
  }

  async apply(action: Action): Promise<{ ok: boolean; error?: string }> {
    const r = await api<{ ok: boolean; error?: string; frame: RemoteFrame }>("POST", `/api/attempts/${this.attemptId}/action`, { action });
    if (r.frame) this.last = r.frame;
    return { ok: r.ok, error: r.error };
  }

  record(capture: unknown, atMs?: number): void {
    // Each sample carries its bench time so speeds can be computed later.
    this.pending.push(atMs === undefined ? capture : { ...(capture as object), t: Math.round(atMs) });
    if (this.pending.length >= 50) void this.flush();
    else if (this.flushTimer === undefined) this.flushTimer = window.setTimeout(() => void this.flush(), 3000);
  }

  async flush(): Promise<void> {
    window.clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    if (this.pending.length === 0) return;
    const batch = this.pending.splice(0, 500);
    try {
      await api("POST", `/api/attempts/${this.attemptId}/capture`, batch);
    } catch {
      /* drop the batch */
    }
  }

  async recordReading(): Promise<RecordOutcome> {
    const r = await api<{ outcome: RecordOutcome; frame: RemoteFrame }>("POST", `/api/attempts/${this.attemptId}/record`);
    if (r.frame) this.last = r.frame;
    return r.outcome;
  }

  async recordScope(): Promise<RecordOutcome> {
    const r = await api<{ outcome: RecordOutcome; frame: RemoteFrame }>("POST", `/api/attempts/${this.attemptId}/record-scope`);
    if (r.frame) this.last = r.frame;
    return r.outcome;
  }

  async predict(goal: string, value: number): Promise<{ ok: boolean; correct?: boolean; error?: string }> {
    const r = await api<{ ok: boolean; correct?: boolean; error?: string; frame: RemoteFrame }>("POST", `/api/attempts/${this.attemptId}/predict`, { goal, value });
    if (r.frame) this.last = r.frame;
    return r;
  }

  hint(): HintView | null {
    return null; // hints are off in certificate mode
  }

  /** Finish the stage: next activity, or the result and certificate. */
  async next(): Promise<{ status: string; frame?: RemoteFrame; certificate?: string; url?: string; score?: number; maxScore?: number; error?: string }> {
    await this.flush();
    const r = await api<{ status: string; frame?: RemoteFrame; certificate?: string; url?: string; score?: number; maxScore?: number; error?: string }>("POST", `/api/attempts/${this.attemptId}/next`);
    if (r.frame) this.last = r.frame;
    return r;
  }

  async abandon(): Promise<void> {
    await this.flush();
    await api("POST", `/api/attempts/${this.attemptId}/abandon`).catch(() => undefined);
  }
}

export type { View, ActivityView };

// --- account and progress ----------------------------------------------------

export interface LinkedKey { code: string; label: string; professor: string; institution: string; linkedAt: string }
export interface Notification { id: string; text: string; at: string }
export interface Me {
  user: { id: string; email: string; displayName: string; role: string } | null;
  keys?: LinkedKey[];
  notifications?: Notification[];
  professorStatus?: string | null;
}
export interface KeyLookup { code: string; label: string; professor: string; institution: string; verified: boolean; open: boolean; problem: string | null; confirmation: string }
export interface Providers { google: boolean; microsoft: boolean; dev: boolean }
export interface LevelProgress {
  level: number; name: string; activities: string[]; practised: boolean[]; certificateActivities: string[];
  unlocked: boolean; ready: boolean; certificate: { code: string; issuedAt: string } | null;
}
export interface Progress { units: { unit: number; name: string; levels: LevelProgress[] }[] }

export const account = {
  me: () => api<Me>("GET", "/api/me"),
  providers: () => api<Providers>("GET", "/auth/providers"),
  progress: () => api<Progress>("GET", "/api/progress"),
  logout: () => api("POST", "/auth/logout"),
  devLogin: (email: string, name: string) => api("POST", "/auth/dev", { email, name }),
  uploadPractice: (body: unknown) => api("POST", "/api/practice", body),
  updateMe: (displayName: string) => api("PUT", "/api/me", { displayName }),
  lookupKey: (code: string) => api<KeyLookup>("GET", `/api/keys/${encodeURIComponent(code)}`),
  joinKey: (code: string) => api<{ ok: boolean; confirmation?: string; error?: string }>("POST", `/api/keys/${encodeURIComponent(code)}/join`),
  leaveKey: (code: string) => api("POST", `/api/keys/${encodeURIComponent(code)}/leave`),
  readNotifications: () => api("POST", "/api/notifications/read"),
};

// --- professors and administrators (spec 11) ---------------------------------

export interface ProfessorApplication {
  name: string; institution: string; department: string; directoryUrl: string;
  status: string; submittedAt: string; decidedAt: string | null; decisionNote: string;
}
export interface ClassKey { id: string; code: string; label: string; createdAt: string; expiresAt: string | null; retiredAt: string | null; students: number }
export interface ProfessorHome { user: NonNullable<Me["user"]>; application: ProfessorApplication | null; keys: ClassKey[] }
export interface CertificateSummary { code: string; unit: number; level: number; levelName: string; issuedAt: string; score: number; maxScore: number; attemptId: string; url: string }
export interface StudentRow { id: string; name: string; email: string; linkedAt: string; certificates: CertificateSummary[]; attempts: number }
export interface AttemptSummary { id: string; unit: number; level: number; levelName: string; status: string; startedAt: string; finishedAt: string | null; score: number; maxScore: number; inputDevices: string[] }
export interface PracticeSummary { activityId: string; startedAt: string; finishedAt: string; score: number; maxScore: number; complete: boolean; passed: boolean }
export interface StudentDetail { student: { id: string; name: string; email: string }; certificates: CertificateSummary[]; attempts: AttemptSummary[]; practice: PracticeSummary[] }
export interface AttemptLogEntry { seq: number; stage: number; serverTime: string; simTime: number; entry: Record<string, unknown> & { entry: string } }
export interface AttemptDetail {
  attempt: AttemptSummary & { seed: string; stages: string[]; student: { id: string; name: string }; certificate: string | null };
  log: AttemptLogEntry[];
  flags: { kind: string; stage: number | null; text: string }[];
  anomalies: AnomalyFlag[];
  anomaliesVisible: boolean;
}
export interface AnomalyFlag { kind: string; stage: number; details: { outliers: Record<string, { value: number; median: number; mad: number; z: number }>; baseline: number }; at: string }
export interface AnomalyRow extends AnomalyFlag { id: string; attemptId: string; student: { id: string; name: string }; unit: number; level: number }
export interface BaselineRow { activityId: string; title: string; sessions: number; pilot: number; ready: boolean; medians: Record<string, { median: number; mad: number; n: number }> }
export interface ApplicationRow extends ProfessorApplication {
  id: string; userId: string; note: string; email: string; emailDomain: string; role: string; vouches: { name: string; institution: string }[];
}
export interface AccessEntry { id: number; at: string; professor: { id: string; name: string }; student: { id: string; name: string } | null; kind: string; target: string }

export const professor = {
  home: () => api<ProfessorHome>("GET", "/api/professor"),
  apply: (body: { name: string; institution: string; department: string; directoryUrl: string; note: string }) => api<{ ok?: boolean; error?: string }>("POST", "/api/professor/apply", body),
  vouch: (email: string) => api<{ ok?: boolean; error?: string }>("POST", "/api/professor/vouch", { email }),
  createKey: (label: string, expiresAt: string | null) => api<{ ok?: boolean; code?: string; error?: string }>("POST", "/api/professor/keys", { label, expiresAt }),
  retireKey: (id: string) => api("POST", `/api/professor/keys/${id}/retire`),
  students: (keyId: string) => api<{ key: { id: string; code: string; label: string }; students: StudentRow[] }>("GET", `/api/professor/keys/${keyId}/students`),
  removeStudent: (keyId: string, studentId: string) => api("DELETE", `/api/professor/keys/${keyId}/students/${studentId}`),
  student: (id: string) => api<StudentDetail>("GET", `/api/professor/students/${id}`),
  attempt: (id: string) => api<AttemptDetail>("GET", `/api/professor/attempts/${id}`),
};

export const admin = {
  applications: (status: string) => api<{ applications: ApplicationRow[] }>("GET", `/api/admin/applications?status=${status}`),
  decide: (id: string, decision: string, note: string) => api<{ ok?: boolean; error?: string }>("POST", `/api/admin/applications/${id}/decide`, { decision, note }),
  accessLog: (limit = 200) => api<{ entries: AccessEntry[] }>("GET", `/api/admin/access-log?limit=${limit}`),
  baselines: () => api<{ baselines: BaselineRow[]; minBaseline: number }>("GET", "/api/admin/baselines"),
  anomalies: (limit = 100) => api<{ anomalies: AnomalyRow[] }>("GET", `/api/admin/anomalies?limit=${limit}`),
  recompute: () => api<{ ok: boolean; practice: number; attempts: number; flagged: number }>("POST", "/api/admin/anomalies/recompute"),
};
